import React, { FC, useState } from 'react';
import { T } from 'src/shared/helpers';

import { CollapsibleSection } from '../common/collapsibleSection';
import { TOOL_DEFINITIONS, getToolInfo } from '../common/constants';
import {
    ConditionalBadge,
    EmptyState,
    InfoIcon,
    ItemAction,
    ItemActionDanger,
    ItemDot,
    ItemMeta,
    ItemName,
    ItemNameAction,
    LearnMoreLink,
    PanelItem,
    PanelItemDimmed,
    PanelItemSpacedLarge,
    PriceBadge,
    StructureSection,
    StructureSectionHeader,
    StructureSectionTitle,
    StructureSummaryCard,
    StructureSummaryGrid,
    StructureSummaryLabel,
    StructureSummaryValue,
    StructureTransitionPanel,
    StructureTransitionWrapper,
    StructureTree,
    StructureTreeToggle,
    TreeIcon,
    TreeManageLink,
    TreeSide,
    TreeVariant,
    TreeVariantHeader
} from '../common/styled';
import type { AddPanelKey, ProductToolData, SidePanelProduct, ToolDefinition } from '../common/types';
import { useConfiguratorData } from '../common/useConfiguratorData';
import { PanelTextSkeleton } from '../common/skeletons';

export interface ConfiguratorStructureSectionProps {
    product: SidePanelProduct;
    hasOptionPrice: (opt: { price: number | null; optionCode: string }) => boolean;
    toolData: ProductToolData;
    accountPricingRules: Array<{ id: string; name: string }>;
    isToolAllowed: (tool: ToolDefinition) => boolean;
    expandAllKey: number;
    openSections: Set<string>;
    isUnassigning: boolean;
    unassigningItemId: string | null;
    onOpenSectionChange: (toolKey: string, isOpen: boolean) => void;
    onOpenToolEditor: (toolKey: string, itemId: string) => void;
    onUnassignTool: (toolKey: string, itemId: string, itemName: string) => void;
    onShowAddPanel: (key: AddPanelKey) => void;
    onGoToFullTool: (route: string, id?: string) => void;
    onShowHelpPanel?: (url: string) => void;
}

export const ConfiguratorStructureSection: FC<ConfiguratorStructureSectionProps> = ({
    product,
    hasOptionPrice,
    toolData,
    accountPricingRules,
    isToolAllowed,
    expandAllKey,
    openSections,
    isUnassigning,
    unassigningItemId,
    onOpenSectionChange,
    onOpenToolEditor,
    onUnassignTool,
    onShowAddPanel,
    onGoToFullTool,
    onShowHelpPanel
}) => {
    const configuratorData = useConfiguratorData(product.id);
    const toolDefinitions = TOOL_DEFINITIONS;
    const [showComposerTree, setShowComposerTree] = useState(false);
    const renderNoPermission = () => <EmptyState>{T._("You don't have permission to access this tool.", 'Admin')}</EmptyState>;

    const customizableAreas = configuratorData.sceneMeshes.reduce((sum, mesh) => sum + mesh.sides.length, 0);

    return (
        <>
            <StructureSection>
                <StructureSectionHeader>
                    <StructureSectionTitle>
                        {T._('3D Scene', 'Admin')}
                        <InfoIcon
                            href='https://zakeke.zendesk.com/hc/en-us/articles/360009461214-Scene-Editor'
                            target='_blank'
                            rel='noopener'
                            title={T._('Guide', 'Admin')}
                        >
                            i
                        </InfoIcon>
                    </StructureSectionTitle>
                </StructureSectionHeader>
                <EmptyState>
                    {T._('Manage the 3D model, meshes, materials, lights, cameras and environment in the', 'Admin')}{' '}
                    <LearnMoreLink
                        as='span'
                        onClick={() => {
                            window.location.href = `/Composer?id=${product.modelID}&page=product`;
                        }}
                        style={{ cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}
                    >
                        {T._('3D Scene Editor', 'Admin')} &#8250;
                    </LearnMoreLink>
                </EmptyState>
            </StructureSection>
            <StructureSection>
                <StructureSectionHeader>
                    <StructureSectionTitle>
                        {T._('Attributes & Options', 'Admin')}
                        <InfoIcon
                            href='https://zakeke.zendesk.com/hc/en-us/articles/360009576134-Composer'
                            target='_blank'
                            rel='noopener'
                            title={T._('Guide', 'Admin')}
                        >
                            i
                        </InfoIcon>
                    </StructureSectionTitle>
                    <StructureTreeToggle onClick={() => setShowComposerTree(!showComposerTree)}>
                        {showComposerTree ? '▴' : '▾'} {T._('Details', 'Admin')}
                    </StructureTreeToggle>
                </StructureSectionHeader>
                <StructureTransitionWrapper>
                    <StructureTransitionPanel $visible={!showComposerTree}>
                        <StructureSummaryGrid>
                            <StructureSummaryCard>
                                <StructureSummaryValue>{configuratorData.attributes.length}</StructureSummaryValue>
                                <StructureSummaryLabel>
                                    {configuratorData.attributes.length === 1 ? T._('Attribute', 'Admin') : T._('Attributes', 'Admin')}
                                </StructureSummaryLabel>
                            </StructureSummaryCard>
                            <StructureSummaryCard>
                                <StructureSummaryValue>{configuratorData.attributes.reduce((sum, attr) => sum + attr.options.length, 0)}</StructureSummaryValue>
                                <StructureSummaryLabel>{T._('Options', 'Admin')}</StructureSummaryLabel>
                            </StructureSummaryCard>
                        </StructureSummaryGrid>
                    </StructureTransitionPanel>
                    <StructureTransitionPanel $visible={showComposerTree}>
                        <StructureTree>
                            {configuratorData.attributes.map((attr, i) => (
                                <TreeVariant key={i}>
                                    <TreeVariantHeader>
                                        <TreeIcon>&#9724;</TreeIcon>
                                        <span>{attr.name}</span>
                                        <ItemMeta>
                                            {attr.options.length} {attr.options.length === 1 ? T._('option', 'Admin') : T._('options', 'Admin')}
                                        </ItemMeta>
                                    </TreeVariantHeader>
                                    {attr.options.map((opt, oIdx) => (
                                        <TreeSide key={oIdx}>
                                            <TreeIcon>&#9679;</TreeIcon>
                                            <span>{opt.name}</span>
                                            {hasOptionPrice(opt) && <PriceBadge title={`${opt.price! > 0 ? '+' : ''}${opt.price}`}>&#36;</PriceBadge>}
                                            {opt.hasLink && <ConditionalBadge title={T._('Conditional rule', 'Admin')}>&#8645;</ConditionalBadge>}
                                        </TreeSide>
                                    ))}
                                </TreeVariant>
                            ))}
                        </StructureTree>
                        <TreeManageLink>
                            <a href={`/Composer?id=${product.modelID}&page=product&subpage=composer`}>{T._('Manage', 'Admin')}</a>
                        </TreeManageLink>
                    </StructureTransitionPanel>
                </StructureTransitionWrapper>
            </StructureSection>
            <StructureSection>
                <StructureSectionHeader>
                    <StructureSectionTitle>
                        {T._('Personalization', 'Admin')}
                        <InfoIcon
                            href='https://zakeke.zendesk.com/hc/en-us/articles/10734802189340-Pre-designed-templates-for-Configurable-products'
                            target='_blank'
                            rel='noopener'
                            title={T._('Guide', 'Admin')}
                        >
                            i
                        </InfoIcon>
                    </StructureSectionTitle>
                </StructureSectionHeader>
                <StructureSummaryGrid>
                    <StructureSummaryCard>
                        <StructureSummaryValue>{customizableAreas}</StructureSummaryValue>
                        <StructureSummaryLabel>
                            {customizableAreas === 1 ? T._('Customizable Area', 'Admin') : T._('Customizable Areas', 'Admin')}
                        </StructureSummaryLabel>
                    </StructureSummaryCard>
                </StructureSummaryGrid>
                <EmptyState>
                    {T._('Manage print areas, sizes and camera positions in the', 'Admin')}{' '}
                    <LearnMoreLink
                        as='span'
                        onClick={() => onGoToFullTool(`/Composer?id=${product.modelID}&page=product&subpage=designs`)}
                        style={{ cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}
                    >
                        {T._('Configurator Editor', 'Admin')} &#8250;
                    </LearnMoreLink>
                </EmptyState>

                <div style={{ marginTop: 2 }} />
                {(() => {
                    const pricingTool = toolDefinitions.find((t) => t.key === 'pricing')!;
                    const pricingInfo = getToolInfo(product, pricingTool, toolData);
                    const canAccessPricing = isToolAllowed(pricingTool);
                    const hasItems = pricingInfo.items.length > 0;
                    const assignedPricingIds = new Set((toolData.pricingRules || []).map((r) => r.id));
                    const hasAssignablePricingRules = assignedPricingIds.size === 0 && accountPricingRules.some((r) => !assignedPricingIds.has(r.id));
                    const handlePricingAddClick = () => {
                        if (hasAssignablePricingRules) {
                            onShowAddPanel('pricingRule');
                        } else if (pricingTool.addPanelKey && accountPricingRules.length > 0) {
                            onShowAddPanel('pricingRule');
                        } else {
                            onOpenToolEditor('pricing', '');
                        }
                    };
                    return (
                        <CollapsibleSection
                            key={`pricing-${expandAllKey}`}
                            title={pricingTool.name}
                            count={canAccessPricing ? pricingInfo.count : 0}
                            isBoolean={pricingTool.isBoolean}
                            controlledOpen={openSections.has('pricing')}
                            helpUrl={pricingTool.helpUrl}
                            onOpenChange={(isOpen) => onOpenSectionChange('pricing', isOpen)}
                        >
                            {!canAccessPricing ? (
                                renderNoPermission()
                            ) : !toolData.loaded ? (
                                <PanelTextSkeleton />
                            ) : hasItems ? (
                                <>
                                    <EmptyState>
                                        {T._('These rules apply to the design part.', 'Admin')} {T._('For pricing the configuration part, follow', 'Admin')}{' '}
                                        <LearnMoreLink
                                            href={
                                                onShowHelpPanel
                                                    ? undefined
                                                    : 'https://zakeke.zendesk.com/hc/en-us/articles/360009576134-Composer#h_01G0M6P43K3A6F8NHZBFVHP02J'
                                            }
                                            target={onShowHelpPanel ? undefined : '_blank'}
                                            rel={onShowHelpPanel ? undefined : 'noopener'}
                                            as={onShowHelpPanel ? 'span' : undefined}
                                            onClick={
                                                onShowHelpPanel
                                                    ? () =>
                                                          onShowHelpPanel(
                                                              'https://zakeke.zendesk.com/hc/en-us/articles/360009576134-Composer#h_01G0M6P43K3A6F8NHZBFVHP02J'
                                                          )
                                                    : undefined
                                            }
                                            style={onShowHelpPanel ? { cursor: 'pointer' } : undefined}
                                        >
                                            {T._('this guide', 'Admin')} &#8250;
                                        </LearnMoreLink>
                                    </EmptyState>
                                    {pricingInfo.items.slice(0, 10).map((item) => {
                                        const isItemUnassigning = isUnassigning && unassigningItemId === item.id;
                                        return (
                                            <PanelItemDimmed
                                                key={item.id}
                                                $clickable={!isItemUnassigning}
                                                onClick={() => !isItemUnassigning && onOpenToolEditor('pricing', item.id)}
                                                $dimmed={isItemUnassigning}
                                            >
                                                <ItemDot $color='#3b82f6' />
                                                <ItemName>{item.name}</ItemName>
                                                <ItemActionDanger
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onUnassignTool('pricing', item.id, item.name);
                                                    }}
                                                >
                                                    {T._('Unassign', 'Admin')}
                                                </ItemActionDanger>
                                                <ItemAction>{T._('Edit', 'Admin')} &#8250;</ItemAction>
                                            </PanelItemDimmed>
                                        );
                                    })}
                                    {pricingInfo.items.length > 10 && (
                                        <PanelItem>
                                            <ItemMeta>
                                                +{pricingInfo.items.length - 10} {T._('more', 'Admin')}
                                            </ItemMeta>
                                        </PanelItem>
                                    )}
                                </>
                            ) : (
                                <>
                                    <EmptyState>
                                        {T._('Create price markup for the design part of your product.', 'Admin')}{' '}
                                        {T._('For pricing the configuration part, follow', 'Admin')}{' '}
                                        <LearnMoreLink
                                            href={
                                                onShowHelpPanel
                                                    ? undefined
                                                    : 'https://zakeke.zendesk.com/hc/en-us/articles/360009576134-Composer#h_01G0M6P43K3A6F8NHZBFVHP02J'
                                            }
                                            target={onShowHelpPanel ? undefined : '_blank'}
                                            rel={onShowHelpPanel ? undefined : 'noopener'}
                                            as={onShowHelpPanel ? 'span' : undefined}
                                            onClick={
                                                onShowHelpPanel
                                                    ? () =>
                                                          onShowHelpPanel(
                                                              'https://zakeke.zendesk.com/hc/en-us/articles/360009576134-Composer#h_01G0M6P43K3A6F8NHZBFVHP02J'
                                                          )
                                                    : undefined
                                            }
                                            style={onShowHelpPanel ? { cursor: 'pointer' } : undefined}
                                        >
                                            {T._('this guide', 'Admin')} &#8250;
                                        </LearnMoreLink>
                                    </EmptyState>
                                    <PanelItemSpacedLarge $clickable onClick={handlePricingAddClick}>
                                        <ItemDot $color='#d1d5db' />
                                        <ItemNameAction>+ {accountPricingRules.length > 0 ? T._('Add', 'Admin') : T._('Set up', 'Admin')}</ItemNameAction>
                                    </PanelItemSpacedLarge>
                                </>
                            )}
                        </CollapsibleSection>
                    );
                })()}
            </StructureSection>
        </>
    );
};
